/**
 * The v1 sender's own context: the Segment facade and the four ambient traits
 * every legacy event carries.
 *
 * These lived in `@/models/analyticsScribeModel`, which is why
 * `./track-legacy-event` imported the model — and why every emit host under
 * `./scribe/` that has a v1 leg would otherwise import it too, dragging the
 * flag registry and the API layer in behind it. Nothing here needs the store,
 * so nothing here belongs in a Rematch model. The Segment load/Braze gate and
 * the adapter registration, which do read auth state, live in `./segment-boot`.
 */
import { installSourceContextAtom } from '@/atoms/install-source';
import { isTauri } from '@/tauri/utils';
import { AppType } from '@/types';
import { getDistributionChannel } from '@/utils/appConfiguration';
import { isSaasTelemetryEnabled } from '@/utils/buildProfile';
import { AnalyticsBrowser } from '@segment/analytics-next';
import { getDefaultStore } from 'jotai';

// On-prem build: load with `disable: true` (officially supported no-op mode in
// @segment/analytics-next ≥ 1.55) — no analytics.min.js fetch, no API egress,
// AND no in-memory queue growth. On-prem must call load() immediately: a bare
// `new AnalyticsBrowser()` that never load()s would buffer
// track()/page()/identify() calls indefinitely on the private
// AnalyticsBuffered queue.
//
// Cloud build: construct the buffered facade at module scope (cheap)
// and defer `.load()` — which fetches + evals the remote analytics.min.js — to
// post-load idle. This is Segment's official lazy-load pattern: calls made
// before load() queue on the facade and flush once it runs, so no events drop.
// TELEMETRY-GATE: a disabled facade when telemetry is suppressed.
export const segmentClient = isSaasTelemetryEnabled
  ? new AnalyticsBrowser()
  : AnalyticsBrowser.load({ writeKey: '' }, { disable: true });

export const getAppType = () => {
  if (isTauri()) {
    return AppType.Desktop;
  }
  return AppType.Web;
};

export const getPlatform = (): 'web' | 'desktop' | 'ios' | 'android' => {
  switch (getAppType()) {
    case AppType.Desktop:
      return 'desktop';
    case AppType.Web:
    default:
      return 'web';
  }
};

interface ScreenDimensions {
  screen_width: number;
  screen_height: number;
  viewport_width: number;
  viewport_height: number;
  device_pixel_ratio: number;
}

export const getScreenDimensions = (): ScreenDimensions | Record<string, never> => {
  if (typeof window === 'undefined') return {};
  return {
    screen_width: window.screen.width,
    screen_height: window.screen.height,
    viewport_width: window.innerWidth,
    viewport_height: window.innerHeight,
    device_pixel_ratio: window.devicePixelRatio,
  };
};

export const getInstallSourceAnalyticsTraits = () => {
  const installSourceContext = getDefaultStore().get(installSourceContextAtom);
  return {
    distribution_channel: getDistributionChannel(),
    mdm_provider: installSourceContext?.mdmProvider ?? null,
  };
};
